import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { HostAction, NavigateTarget } from '../protocol.ts';
import { type ApiFetch, createApiFetch } from './api-fetch.ts';
import { type AppBridge, type BridgeWindow, type DevFallback, HostBridge, type HostState } from './bridge.ts';

interface HostContextValue {
  bridge: AppBridge;
  apiFetch: ApiFetch;
}

const HostContext = createContext<HostContextValue | null>(null);

/** The bridge of the mounted provider, for `hostActions` outside React. */
let activeBridge: AppBridge | null = null;

export interface W3sendHostProviderProps {
  /** Exact dashboard origins allowed to talk to this app (`W3SEND_DASHBOARD_ORIGINS`). */
  allowedOrigins: readonly string[];
  /**
   * A ready-made bridge to use instead of the W3Chat HostBridge, e.g. a ChatwootBridge for a stock
   * Chatwoot-based CRM. The options below that configure HostBridge are then ignored.
   */
  bridge?: AppBridge;
  /**
   * Token source when the app is opened outside the dashboard. Honoured only in development
   * builds; pass it as `import.meta.env.DEV ? {...} : undefined` so it is stripped from production.
   */
  devFallback?: DevFallback | undefined;
  /** Prefix for relative `useApiFetch()` inputs, e.g. `${import.meta.env.BASE_URL}api`. */
  apiBaseUrl?: string;
  /** Follow the dashboard theme by toggling `dark` on <html>. Default true. */
  syncTheme?: boolean;
  onStandaloneAction?: (action: HostAction) => void;
  /** Testing seams. */
  isDev?: boolean;
  hostWindow?: BridgeWindow;
  handshakeTimeoutMs?: number;
  refreshTimeoutMs?: number;
  children: ReactNode;
}

/**
 * Performs the dashboard handshake and makes the app context and session available to the tree.
 * Mount it once, at the root.
 */
export function W3sendHostProvider(props: W3sendHostProviderProps) {
  const { children, apiBaseUrl = '' } = props;
  // The bridge is created once; later prop changes are ignored on purpose (origins are config).
  const [bridge] = useState<AppBridge>(
    () =>
      props.bridge ??
      new HostBridge({
        allowedOrigins: props.allowedOrigins,
        devFallback: props.devFallback,
        isDev: props.isDev,
        syncTheme: props.syncTheme,
        onStandaloneAction: props.onStandaloneAction,
        window: props.hostWindow,
        handshakeTimeoutMs: props.handshakeTimeoutMs,
        refreshTimeoutMs: props.refreshTimeoutMs,
      }),
  );

  useEffect(() => {
    bridge.start();
    activeBridge = bridge;
    return () => {
      bridge.stop();
      if (activeBridge === bridge) activeBridge = null;
    };
  }, [bridge]);

  const value = useMemo(
    () => ({ bridge, apiFetch: createApiFetch(bridge, apiBaseUrl) }),
    [bridge, apiBaseUrl],
  );
  return <HostContext.Provider value={value}>{children}</HostContext.Provider>;
}

function useHost(): HostContextValue {
  const value = useContext(HostContext);
  if (!value) throw new Error('Wrap the app in <W3sendHostProvider>');
  return value;
}

/** Account, current agent, conversation/contact (conversation tabs), theme and handshake status. */
export function useHostContext(): HostState {
  const { bridge } = useHost();
  return useSyncExternalStore(bridge.subscribe, bridge.getState, bridge.getState);
}

/** A fetch to the app's own backend that carries the session token and refreshes it on 401. */
export function useApiFetch(): ApiFetch {
  return useHost().apiFetch;
}

export interface HostActions {
  navigate: (target: NavigateTarget, id: number) => void;
  toast: (message: string) => void;
  openUrl: (url: string) => void;
  /** Opens this app's own sidebar page at `path` (e.g. from a conversation tab). */
  openApp: (path: string) => void;
  /** Conversation tab: puts `text` (≤ 4096 characters) into the reply box for the agent to send. */
  insertReply: (text: string) => void;
}

export function useHostActions(): HostActions {
  const { bridge } = useHost();
  return useMemo(
    () => ({
      navigate: (target, id) => bridge.navigate(target, id),
      toast: (message) => bridge.toast(message),
      openUrl: (url) => bridge.openUrl(url),
      openApp: (path) => bridge.openApp(path),
      insertReply: (text) => bridge.insertReply(text),
    }),
    [bridge],
  );
}

/**
 * Follows the dashboard's `w3send-app:route` events (another frame of this app asked to show a
 * page here): `onRoute` is called with the path, including one that arrived before mounting.
 * Typically: `useHostRoute((path) => router.navigate(path))`.
 */
export function useHostRoute(onRoute: (path: string) => void): void {
  const { bridge } = useHost();
  const handler = useRef(onRoute);
  useEffect(() => {
    handler.current = onRoute;
  });
  useEffect(() => bridge.onRoute((path) => handler.current(path)), [bridge]);
}

const withBridge = (run: (bridge: AppBridge) => void) => {
  if (activeBridge) run(activeBridge);
  else console.warn('[w3send] hostActions used before <W3sendHostProvider> mounted');
};

/** Host actions usable outside components (event handlers in plain modules). */
export const hostActions: HostActions = {
  navigate: (target, id) => withBridge((bridge) => bridge.navigate(target, id)),
  toast: (message) => withBridge((bridge) => bridge.toast(message)),
  openUrl: (url) => withBridge((bridge) => bridge.openUrl(url)),
  openApp: (path) => withBridge((bridge) => bridge.openApp(path)),
  insertReply: (text) => withBridge((bridge) => bridge.insertReply(text)),
};
